import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CookieService } from './index';
import { RoleName, User } from '../models';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  userData: any;
  headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
  isLoginSubject = new BehaviorSubject<boolean>(this.isValidUser());
  isClientSubject = new BehaviorSubject<boolean>(false);
  isAdminSubject = new BehaviorSubject<boolean>(this.getLoggedAdminRole());

  constructor(
    private http: HttpClient,
    private router: Router,
    private jwtHelper: JwtHelperService,
    private cookieService: CookieService
  ) {}

  /**
   * Returns the current user
   */
  currentUser() {
    return this.userData;
  }

  /**
   * Checks the status of the token
   * @return boolean
   */
  isTokenExpired() {
    return this.userData ? this.jwtHelper.isTokenExpired(this.userData['access_token']) : false;
  }

  /**
   * User is loggedIn and token is valid
   * @return boolean
   */
  isValidUser() {
    this.userData = JSON.parse(this.cookieService.getCookie('currentUser') as string);
    return !!this.userData && !this.isTokenExpired();
  }

  /**
   * Checks if user is logged in
   * @returns {Observable<boolean>}
   */
  isLoggedIn(): Observable<boolean> {
    return this.isLoginSubject.asObservable();
  }

  /**
   * Checks if client is logged in
   * @returns {Observable<boolean>}
   */
  isClientLoggedIn(): Observable<boolean> {
    return this.isClientSubject.asObservable();
  }

  /**
   * Checks if admin is logged in
   * @returns {Observable<boolean>}
   */
  isAdminLoggedIn(): Observable<boolean> {
    return this.isAdminSubject.asObservable();
  }

  /**
   * Register user
   * @return server response
   */
  register(userData: User) {
    return this.http.post(
      'register',
      userData,
      {headers: this.headers})
      .pipe(map(response => response));
  }

  /**
   * Sends Verification email to the registered user
   * @param id for
   * @return server response
   */
  sendVerification(id: number) {
    return this.http.post(
      'email/resend',
      {id},
      {headers: this.headers})
      .pipe(map(response => response));
  }

  /**
   * Sends Verification credentials to server
   * @param queryURL to the server
   */
  verifyEmail(queryURL: string) {
    return this.http.get(queryURL).pipe(map(response => response));
  }

  /**
   * Performs the auth
   * @param email email of user
   * @param password password of user
   */
   login(email: User, password: User) {
     return this.http.post<any>(
       'login',
       { email, password },
       {headers: this.headers})
       .pipe(map(user => {
         if (user && user['access_token']) {
           this.userData = user;
           this.cookieService.setCookie('currentUser', JSON.stringify(user), 1);
           this.isLoginSubject.next(true);
           this.isClientSubject.next(false);
          this.isAdminSubject.next(this.getLoggedAdminRole());
         }
         return user;
       }));
   }

  /**
   * Logout the user
   */
  logout() {
    this.cookieService.deleteCookie('currentUser');
    localStorage.removeItem('cartItems');
    localStorage.removeItem('cartItemQuantities');
    localStorage.removeItem('totalCartCost');
    localStorage.removeItem('quantities');
    this.userData = null;
    this.isLoginSubject.next(false);
    this.isClientSubject.next(false);
    this.isAdminSubject.next(false);
    this.router.navigateByUrl('authenticate/login');
  }

  getLoggedAdminRole() {
    const adminRoles = [
      RoleName.ADMIN_ONE,
      RoleName.ADMIN_TWO,
      RoleName.ADMIN_THREE,
      RoleName.ADMIN_FOUR
    ];
    if (this.currentUser()) {
      const { user } = this.currentUser() as any;
      return adminRoles.includes(user.role?.name);
    }
    return false;
  }  
}
